import { BackendStudyOffer } from './StudyOffersService';
import PulseService, { Unsubscribe } from '@/services/pulse/PulseService';

export type OfferCreatedHandler = (offer: BackendStudyOffer) => void;

// Legacy Echo-like payload
type OfferCreatedPayloadLegacy = {
  id: number;
  study_id: number;
  region_id?: number;
  notes?: string | null;
  expires_at: string;
  created_at?: string;
  study?: {
    contact_id?: number | null;
    contact?: {
      id: number;
      name?: string | null;
      address_lat?: number | null;
      address_lng?: number | null;
    };
  };
};

// Proposed Pulse payload: { offer: {...}, offered_by: {...} }
type OfferCreatedPayloadPulse = {
  offer: {
    id: number;
    study_id: number;
    region_id?: number;
    notes?: string | null;
    expires_at: string;
    created_at?: string;
    study?: {
      id: number;
      contact_id?: number | null;
      contact?: {
        id: number;
        name?: string | null;
        address_lat?: number | null;
        address_lng?: number | null;
      };
    };
  };
  offered_by?: { id: number; name?: string | null };
};

// Accepted payload (detailed) recommended by backend
type OfferAcceptedPayload = {
  offer_id: number;
  offered_by_user_id?: number;
  accepted_by?: { id: number; name?: string };
  region_id?: number;
};

function isLegacyCreatedPayload(x: unknown): x is OfferCreatedPayloadLegacy {
  const p = x as OfferCreatedPayloadLegacy | undefined;
  return !!p && typeof p.id === 'number' && typeof p.study_id === 'number' && typeof p.expires_at === 'string';
}

function isPulseCreatedPayload(x: unknown): x is OfferCreatedPayloadPulse {
  const p = x as OfferCreatedPayloadPulse | undefined;
  return !!p && !!p.offer && typeof p.offer.id === 'number' && typeof p.offer.study_id === 'number' && typeof p.offer.expires_at === 'string';
}

function isAcceptedPayload(x: unknown): x is OfferAcceptedPayload {
  const p = x as OfferAcceptedPayload | undefined;
  return !!p && typeof p.offer_id === 'number';
}

function mapPayloadToBackendOffer(payload: unknown, fallbackRegionId: number): BackendStudyOffer | null {
  try {
    if (isPulseCreatedPayload(payload)) {
      const o = payload.offer;
      return {
        id: o.id,
        study_id: o.study_id,
        offered_by: payload.offered_by?.id ?? 0,
        accepted_by: null,
        region_id: o.region_id ?? fallbackRegionId,
        notes: o.notes ?? null,
        status: 'pending',
        expires_at: o.expires_at,
        accepted_at: null,
        declined_at: null,
        cancelled_at: null,
        created_at: o.created_at,
        updated_at: o.created_at,
        study: o.study ? { id: o.study_id, contact_id: o.study.contact_id, contact: o.study.contact } : undefined,
      };
    }
    if (isLegacyCreatedPayload(payload)) {
      const l = payload;
      return {
        id: l.id,
        study_id: l.study_id,
        offered_by: 0,
        accepted_by: null,
        region_id: l.region_id ?? fallbackRegionId,
        notes: l.notes ?? null,
        status: 'pending',
        expires_at: l.expires_at,
        accepted_at: null,
        declined_at: null,
        cancelled_at: null,
        created_at: l.created_at,
        updated_at: l.created_at,
        study: l.study ? { id: l.study_id, contact_id: l.study.contact_id, contact: l.study.contact } : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to study offer creation events via Pulse.
 * Region scoping é feito pelos canais assinados em PulseBootstrap (region:<id>).
 */
export function subscribeOffers(regionId: number, onCreated: OfferCreatedHandler) {
  // Subscrição por tópico; a filtragem por região ocorre no backend/canal.
  const unsub: Unsubscribe = PulseService.subscribe('offers:created', (payload: unknown) => {
    const offer = mapPayloadToBackendOffer(payload, regionId);
    if (offer) onCreated(offer);
  });
  return { unsubscribe: unsub };
}

// --- Accepted / Cancelled / Expired helpers ---

type OfferMinimalEvent = { offer_id?: number } & Record<string, unknown>;

function extractOfferId(payload: unknown): number | null {
  const p = payload as OfferMinimalEvent | undefined;
  const id = p?.offer_id ?? (p as any)?.id ?? null;
  if (typeof id === 'number') return id;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export function subscribeOfferAccepted(onAccepted: (offerId: number) => void) {
  const unsub = PulseService.subscribe('offers:accepted', (payload: unknown) => {
    const id = extractOfferId(payload);
    if (id != null) onAccepted(id);
  });
  return { unsubscribe: unsub };
}

export function subscribeOfferCancelled(onCancelled: (offerId: number) => void) {
  const unsub = PulseService.subscribe('offers:cancelled', (payload: unknown) => {
    const id = extractOfferId(payload);
    if (id != null) onCancelled(id);
  });
  return { unsubscribe: unsub };
}

export function subscribeOfferExpired(onExpired: (offerId: number) => void) {
  const unsub = PulseService.subscribe('offers:expired', (payload: unknown) => {
    const id = extractOfferId(payload);
    if (id != null) onExpired(id);
  });
  return { unsubscribe: unsub };
}

// Detailed accepted subscription (preserves existing simple API)
export function subscribeOfferAcceptedDetailed(onAccepted: (info: { offerId: number; offered_by_user_id?: number; accepted_by_name?: string }) => void) {
  const unsub = PulseService.subscribe('offers:accepted', (payload: unknown) => {
    if (!isAcceptedPayload(payload)) return;
    const id = payload.offer_id;
    const offeredBy = payload.offered_by_user_id;
    const acceptedBy = payload.accepted_by?.name;
    onAccepted({ offerId: id, offered_by_user_id: offeredBy, accepted_by_name: acceptedBy });
  });
  return { unsubscribe: unsub };
}
